import classnames from 'classnames'
import { Status } from 'embed/components/ApplicationField/Status'
import { Field, FieldData, FieldDataValue } from 'embed/constants'
import { css } from 'emotion'
import * as React from 'react'
import { useTheme } from 'embed/hooks'
import FormAutocomplete, { FormStyles } from '../FormAutocomplete'
import { capturePrescriptionRequestError } from 'ui/multi-product/constants/errors'
import { throttle } from 'throttle-debounce'
import Api from 'ui/multi-product/services/Api'
import { Tooltip } from '../Tooltip'

const ClearIcon = require('assets/components/minus-in-filled-circle.svg')
const InfoSmall = require('../ApplicationField/info-grey.svg')

const ADD_TRIGGER = ','
const INITIAL_MARGIN = 85
const AUTOCOMPLETE_THROTTLE_WAIT = 600
const AUTOCOMPLETE_LIMIT = 20
const PRESCRIPTIONS_API_URL = `/lifeInsurance/prescriptions`

const styles = require('./styles.scss')

interface PrescriptionAutocompleteProps {
  onChange: (value: FieldDataValue) => void
  value?: string[]
  field: Field
  fieldData?: FieldData
  autocompleteStyles?: FormStyles
}

interface PrescriptionItem {
  name: string
  key: string
}

const isInputValid = (value) => value !== undefined && value !== null && value.length > 0

const isValueRequired = (value): boolean => {
  if (Array.isArray(value) && value.length > 0) {
    return false
  }
  return !isInputValid(value)
}
const getShouldShowValidation = (isDirty: boolean, fieldData?: FieldData, value?: string[]): boolean => {
  if (fieldData?.shouldShowValidationStatus) {
    return !fieldData.isValid
  }
  if (Array.isArray(value)) {
    return value.length > 0
  }
  return isDirty
}

export const PrescriptionAutocomplete: React.FC<PrescriptionAutocompleteProps> = ({
  field,
  fieldData,
  onChange,
  value,
  autocompleteStyles,
}) => {
  const [addButtonDisabled, setAddButtonDisabled] = React.useState(true)
  const [inputValue, setInputValue] = React.useState<string>('')
  const [isDirty, setIsDirty] = React.useState(false)
  const [isRequired, setIsRequired] = React.useState(isValueRequired(value))

  const [prescriptionSuggestions, setPrescriptionSuggestions] = React.useState<PrescriptionItem[]>([])
  const abortControllerRef = React.useRef(new AbortController())

  const getPrescriptionPredictionsThrottled = React.useCallback(
    throttle(AUTOCOMPLETE_THROTTLE_WAIT, (textValue: string) => {
      abortControllerRef.current.abort()
      abortControllerRef.current = new AbortController()

      if (!textValue) {
        setPrescriptionSuggestions([])
      } else {
        Api.get(
          PRESCRIPTIONS_API_URL,
          { keyword: textValue, limit: AUTOCOMPLETE_LIMIT },
          abortControllerRef.current.signal,
        )
          .then((res) => {
            if (res.ok) {
              return res.data
            }
            return Promise.reject(res)
          })
          .then((data) => {
            setPrescriptionSuggestions(
              data.map(({ answerValue, answerLabel }) => ({ key: answerValue, name: answerLabel })),
            )
          })
          .catch(capturePrescriptionRequestError)
      }
    }),
    [],
  )

  const handleAdd = React.useCallback(
    (e) => {
      e.preventDefault()
      if (inputValue) {
        const addedValue = inputValue
        const newSet = new Set(Array.isArray(value) ? [...value, addedValue] : [addedValue])
        const newValue = Array.from(newSet)
        setInputValue('')
        setPrescriptionSuggestions([])
        setAddButtonDisabled(true)
        setIsRequired(isValueRequired(newValue))
        onChange(newValue)
      } else {
        setIsDirty(true)
      }
    },
    [inputValue, onChange, value],
  )

  const handleRemove = React.useCallback(
    (val) => {
      if (Array.isArray(value)) {
        const newSet = new Set(value)
        newSet.delete(val)
        const newValue = Array.from(newSet)
        setInputValue('')
        setAddButtonDisabled(true)
        setIsRequired(isValueRequired(newValue))
        onChange(newValue)
      }
    },
    [onChange, value],
  )

  const onInputChange = React.useCallback((event: React.FormEvent<HTMLInputElement>, newValue: string) => {
    if (newValue?.charAt(newValue.length - 1) === ADD_TRIGGER) {
      handleAdd(event)
      return
    }

    getPrescriptionPredictionsThrottled(newValue)

    setInputValue(newValue)
    setAddButtonDisabled(!isInputValid(newValue))
    setIsRequired(isValueRequired(newValue))
  }, [])

  const onSuggestionSelect = React.useCallback((suggestedValue: string) => {
    setInputValue(suggestedValue)
    setAddButtonDisabled(!isInputValid(suggestedValue))
  }, [])

  const {
    borderRadiusSmall,
    backdropStyles,
    colorPrimary,
    colorTextPrimary,
    colorTextSecondary,
    errorStyles,
    fontFamilyPrimary,
    fontSizeBase,
  } = useTheme()
  return (
    <div
      className={classnames(
        css`
          background-color: ${backdropStyles.backgroundColor};
          font-color: ${colorTextPrimary};
          font-family: ${fontFamilyPrimary};
          font-size: ${fontSizeBase};
        `,
      )}
    >
      <div className={styles.freeformLabelContainer}>
        <label className={styles.freeformLabel} htmlFor={field.key}>
          Medication Name
          <Tooltip overlay={field.options.tooltip} overlayClassName={styles.labelTooltip} placement="top">
            <InfoSmall />
          </Tooltip>
        </label>
        <Status
          colorError={errorStyles?.color}
          colorTextSecondary={colorTextSecondary}
          field={field}
          fontFamilyPrimary={fontFamilyPrimary}
          isValid={!isRequired}
          shouldShowIsValid={false}
          shouldShowIsInvalid={getShouldShowValidation(isDirty, fieldData, value)}
          marginRight={INITIAL_MARGIN}
        />
      </div>
      <form onSubmit={handleAdd}>
        <div className={styles.freeformField}>
          <FormAutocomplete
            value={inputValue}
            items={prescriptionSuggestions}
            fieldKey={field.key}
            itemId={(item: PrescriptionItem) => item.key}
            getItemValue={(item: PrescriptionItem) => item.name}
            onChange={onInputChange}
            onSelect={onSuggestionSelect}
            styles={autocompleteStyles}
            containerClass={styles.freeformInput}
          />
          <button
            type="submit"
            className={classnames(
              styles.addButton,
              css`
                background-color: ${colorPrimary};
                border-radius: ${borderRadiusSmall};
                font-family: ${fontFamilyPrimary};
              `,
              addButtonDisabled ? styles.disabled : '',
            )}
          >
            Add
          </button>
        </div>
      </form>

      {getShouldShowValidation(isDirty, fieldData, value) && (
        <div
          className={classnames(
            styles.validation,
            css`
              color: ${errorStyles?.color};
            `,
          )}
        >
          Please add at least one item
        </div>
      )}
      <div className={styles.freeformItemContainer}>
        {Array.isArray(value) &&
          value.map((val, index) => (
            <div key={`div_${val}_${index}`} className={classnames(styles.freeformItem)}>
              <label key={`label_${val}_${index}`} className={classnames(styles.freeformItemLabel)}>
                {val}
              </label>
              <button
                className={classnames(
                  styles.removeButton,
                  css`
                    background-color: ${backdropStyles.backgroundColor};
                    border-radius: ${borderRadiusSmall};
                  `,
                )}
                onClick={() => handleRemove(val)}
              >
                <ClearIcon />
              </button>
            </div>
          ))}
      </div>
    </div>
  )
}
